import { format as formatBase, parseISO, type Locale } from 'date-fns';
import { enUS, fr, es } from 'date-fns/locale'; // Import desired locales

// Map language codes to date-fns locale objects
const locales: { [key: string]: Locale } = {
  en: enUS,
  fr: fr,
  es: es,
};

/**
 * Formats a date string or Date object according to the specified language locale.
 * Uses locale-sensitive formats 'PP' (date) and 'p' (time) by default.
 * 
 * @param dateInput The date to format (ISO string, Date object, or timestamp number).
 * @param language The language code (e.g., 'en', 'fr').
 * @param options Optional format strings for date and time.
 * @returns The formatted date string or 'Invalid Date' if input is invalid.
 */
export const formatLocaleDate = (
  dateInput: string | Date | number | null | undefined,
  language: string,
  options?: { dateFormat?: string; timeFormat?: string; combinedFormat?: string }
): string => {
  if (!dateInput) return 'N/A';

  try {
    let date = typeof dateInput === 'number' ? new Date(dateInput) : typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    
    // Check if the parsed date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        // Try parsing as a standard Date object if parseISO failed
        const directDate = new Date(dateInput);
        if (!(directDate instanceof Date) || isNaN(directDate.getTime())) {
          console.warn('Invalid date input received:', dateInput);
          return 'Invalid Date';
        }
        date = directDate;
    }

    const locale = locales[language] || enUS; // Default to English if locale not found

    // Determine the format string
    let formatString: string;
    if (options?.combinedFormat) {
      formatString = options.combinedFormat;
    } else {
      const dateFormat = options?.dateFormat ?? 'PP'; // Default: Localized Date (e.g., May 4, 2024 / 4 mai 2024)
      const timeFormat = options?.timeFormat ?? 'p'; // Default: Localized Time (e.g., 5:13 PM / 17:13)
      
      // Check if the original input included time information implicitly or explicitly
      // Simple check: does the ISO string contain 'T'? Or is it a Date object?
      const hasTime = (typeof dateInput === 'string' && dateInput.includes('T')) || date instanceof Date; 
                           
      formatString = hasTime ? `${dateFormat} ${timeFormat}` : dateFormat;
    }

    return formatBase(date, formatString, { locale });
  } catch (error) {
    console.error('Error formatting date:', dateInput, error);
    return 'Error';
  }
};

/**
 * Formats a number as currency according to the specified language locale.
 * 
 * @param amount The amount in the smallest currency unit (e.g., cents).
 * @param currency The 3-letter currency code (e.g., 'USD', 'EUR').
 * @param language The language code (e.g., 'en', 'fr').
 * @returns The formatted currency string.
 */
export const formatLocaleCurrency = (
  amount: number,
  currency: string,
  language: string
): string => {
  const localeString = language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US'; // Map simple codes to full locale strings
  try {
    return new Intl.NumberFormat(localeString, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2 // Typically show cents
    }).format(amount / 100);
  } catch (error) {
     console.error('Error formatting currency:', { amount, currency, language }, error);
     // Fallback or simple formatting
     return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}; 